import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import { useToast } from '../components/toast.js'
import { Bar, EmptyState, Notice, Spinner, StatusBadge, Tile } from '../components/ui.js'
import { formatDate, formatDateTime, formatWeekday, initials, relativeDayLabel } from '../lib/format.js'
import { DeleteStaffModal } from './DeleteStaffModal.js'
import { StaffFormModal } from './Staff.js'
import { Icon } from '../components/icons.js'
import { AbsencesCard } from './Absences.js'
import { PageHead } from './Layout.js'
import { CoveringBadge } from './Staff.js'
import { TaskModal } from './TaskDialogs.js'

interface StaffDetail {
  id: string
  name: string
  /** Tasks in the next two weeks done for someone else's plan. */
  coveringTasks?: number
  coveringFor?: { id: string | null; name: string; tasks: number; from: string; until: string }[]
  email: string | null
  phone: string | null
  active: boolean
  planningAccess: boolean
  lastSeenAt: string | null
  today: string
  upcoming: {
    id: string
    title: string
    status: string
    dueDate: string
    dueTime: string | null
    siteName: string
    label: string
    /** Set when this is someone else's plan. */
    coveringForName: string | null
  }[]
  month: { done: number; missed: number; total: number }
  devices: {
    id: string
    label: string | null
    userAgent: string | null
    createdAt: string
    lastSeenAt: string
    revokedAt: string | null
  }[]
}

export function StaffDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [openTask, setOpenTask] = useState<string | null>(null)
  const toast = useToast()
  const [invitation, setInvitation] = useState<{ link: string; code: string; mailed: boolean } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['staff-detail', id],
    queryFn: () => api<StaffDetail>(`/api/staff/${id}`),
    enabled: Boolean(id),
  })
  const location = useLocation()
  // "macht Vertretung" in the staff list links here with #vertretung.
  useEffect(() => {
    if (data && location.hash === '#vertretung') document.getElementById('vertretung')?.scrollIntoView({ block: 'start' })
  }, [data, location.hash])

  const invite = useMutation({
    mutationFn: () => api<{ link: string; code: string; mailed: boolean }>(`/api/staff/${id}/invite`, { method: 'POST' }),
    onSuccess: setInvitation,
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Einladung fehlgeschlagen.', 'error'),
  })

  const setPlanning = useMutation({
    mutationFn: (planningAccess: boolean) => api(`/api/staff/${id}`, { method: 'PATCH', body: { planningAccess } }),
    onSuccess: async (_result, planningAccess) => {
      toast(planningAccess ? 'Tourenplanung im Browser freigeschaltet.' : 'Tourenplanung im Browser abgeschaltet.')
      await queryClient.invalidateQueries({ queryKey: ['staff-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['staff'] })
    },
  })

  const revoke = useMutation({
    mutationFn: (deviceId: string) => api(`/api/devices/${deviceId}/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      toast('Gerät gesperrt. Es hat sofort keinen Zugang mehr.')
      await queryClient.invalidateQueries({ queryKey: ['staff-detail', id] })
    },
  })

  if (isLoading) return <Spinner />
  if (!data) return <Notice kind="error">Mitarbeiter nicht gefunden.</Notice>

  const relevant = data.month.done + data.month.missed
  const quota = relevant > 0 ? Math.round((data.month.done / relevant) * 100) : null

  // Upcoming tasks read best grouped by day rather than as a flat table.
  const groups = new Map<string, StaffDetail['upcoming']>()
  for (const task of data.upcoming) {
    const list = groups.get(task.dueDate)
    if (list) list.push(task)
    else groups.set(task.dueDate, [task])
  }

  return (
    <>
      <PageHead
        title={data.name}
        subtitle={
          <>
            <Link to="/mitarbeiter">Mitarbeiter</Link>, {data.active ? 'aktiv' : 'deaktiviert'}
            {data.coveringTasks ? (
              <>
                {' '}
                <a href="#vertretung" onClick={(e) => { e.preventDefault(); document.getElementById('vertretung')?.scrollIntoView({ behavior: 'smooth' }) }}>
                  <CoveringBadge count={data.coveringTasks} coveringFor={data.coveringFor} />
                </a>
              </>
            ) : null}
          </>
        }
        action={
          <div className="actions">
            <Link className="btn" to={`/mitarbeiter/${data.id}/vertretung`}>
              <Icon name="swap" /> Vertretung planen
            </Link>
            {data.active && (
              <button type="button" className="btn secondary" disabled={invite.isPending} onClick={() => invite.mutate()}>
                Neue Einladung
              </button>
            )}
            <button type="button" className="btn secondary" onClick={() => setEditing(true)}>
              <Icon name="edit" /> Bearbeiten
            </button>
            <button type="button" className="btn ghost" onClick={() => setDeleting(true)}>
              Löschen
            </button>
          </div>
        }
      />
      <StaffFormModal
        open={editing}
        staff={{ id: data.id, name: data.name, email: data.email, phone: data.phone, planningAccess: data.planningAccess }}
        onClose={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />


      {invitation && (
        <Notice kind="success">
          {invitation.mailed ? 'Einladung per E-Mail verschickt. ' : 'Keine E-Mail verschickt, bitte weitergeben: '}
          <br />
          <code>{invitation.link}</code>, Code <strong style={{ letterSpacing: 3 }}>{invitation.code}</strong>
        </Notice>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-body person-head">
          <div className="avatar lg">{initials(data.name)}</div>
          <div className="stack" style={{ gap: 4 }}>
            <div className="strong" style={{ fontSize: 18 }}>{data.name}</div>
            <div className="muted small">
              {data.email ?? 'keine E-Mail'}
              {data.phone ? `, ${data.phone}` : ''}
            </div>
            <div className="muted small">
              Zuletzt aktiv: {data.lastSeenAt ? formatDateTime(data.lastSeenAt) : 'noch nie angemeldet'}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 14 }}>
              <input
                type="checkbox"
                style={{ width: 18, height: 18 }}
                checked={data.planningAccess}
                disabled={setPlanning.isPending}
                onChange={(event) => setPlanning.mutate(event.target.checked)}
              />
              Tourenplanung im Browser
              <span className="muted small">Tagesliste sortieren, Route öffnen, nach Excel; für Touren mit vielen Adressen</span>
            </label>
          </div>
        </div>
      </div>

      <div className="tiles" style={{ marginBottom: 18 }}>
        <Tile label="Erledigt (30 Tage)" value={data.month.done} tone="good" />
        <Tile
          label="Nicht erledigt (30 Tage)"
          value={data.month.missed}
          tone={data.month.missed > 0 ? 'alert' : undefined}
        />
        <Tile
          label="Erfüllungsquote"
          value={quota === null ? '–' : `${quota} %`}
          sub={
            <>
              {data.month.done} von {relevant} fälligen
              <Bar value={data.month.done} total={relevant} />
            </>
          }
        />
        <Tile
          label="Angemeldete Geräte"
          value={data.devices.filter((device) => !device.revokedAt).length}
          sub={data.devices.length === 0 ? 'noch nie angemeldet' : undefined}
        />
      </div>

      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))' }}>
        <AbsencesCard staffId={data.id} />
        {data.coveringFor && data.coveringFor.length > 0 && (
          <section className="card" id="vertretung">
            <div className="card-head">
              <h2>Macht Vertretung für</h2>
              <span className="muted small">nächste 14 Tage, Minijobgrenze beachten</span>
            </div>
            <table className="data">
              <tbody>
                {data.coveringFor.map((c) => (
                  <tr key={c.id ?? c.name}>
                    <td>
                      {c.id ? <Link to={`/mitarbeiter/${c.id}`}>{c.name}</Link> : <span>{c.name}</span>}
                      <div className="muted small">
                        {c.from === c.until ? formatDate(c.from) : `${formatDate(c.from)} bis ${formatDate(c.until)}`}
                      </div>
                    </td>
                    <td className="nowrap">{c.tasks === 1 ? '1 Aufgabe' : `${c.tasks} Aufgaben`}</td>
                    <td className="nowrap" style={{ textAlign: 'right' }}>
                      <Link
                        className="btn secondary small"
                        to={`/aufgaben?mitarbeiter=${data.id}&hinweis=vertretung&von=${c.from}&bis=${c.until}`}
                      >
                        Aufgaben
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <section className="card">
          <div className="card-head">
            <h2>Plan der nächsten Tage</h2>
            <div className="actions">
              <Link className="btn secondary small" to={`/plaene?mitarbeiter=${data.id}`}>
                Aufgabenpläne
              </Link>
              <Link className="btn secondary small" to={`/aufgaben?mitarbeiter=${data.id}`}>
                Aufgaben
              </Link>
            </div>
          </div>
          <div className="card-body">
            {groups.size === 0 ? (
              <EmptyState title="Nichts eingeplant" hint="Diesem Mitarbeiter ist derzeit keine Aufgabe zugewiesen." />
            ) : (
              <ul className="timeline">
                {[...groups.entries()].map(([date, tasks]) => (
                  <li key={date}>
                    <div className={`when ${date === data.today ? 'is-today' : ''}`}>
                      <strong>{dayHeading(date, data.today)}</strong>
                      {formatDate(date)}
                    </div>
                    <div className="stack" style={{ gap: 8 }}>
                      {tasks.map((task) => (
                        <button key={task.id} type="button" className="linklike" onClick={() => setOpenTask(task.id)}>
                          <div className="spread">
                            <span className="strong">{task.title}</span>
                            <StatusBadge status={task.status} />
                          </div>
                          <div className="muted small">
                            {task.siteName}, {task.label}
                            {task.dueTime ? `, bis ${task.dueTime} Uhr` : ''}
                          </div>
                          {task.coveringForName && <div className="small" style={{ color: 'var(--blue-700)' }}>Vertretung für {task.coveringForName}</div>}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h2>Geräte</h2>
          </div>
          {data.devices.length === 0 ? (
            <EmptyState
              title="Noch kein Gerät angemeldet"
              hint="Schicken Sie eine Einladung, der Mitarbeiter meldet sich damit einmalig an."
            />
          ) : (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Angemeldet</th>
                    <th>Zuletzt aktiv</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.devices.map((device) => (
                    <tr key={device.id} style={device.revokedAt ? { opacity: 0.5 } : undefined}>
                      <td className="small">
                        {formatDateTime(device.createdAt)}
                        {device.userAgent && (
                          <div className="muted" style={{ maxWidth: 220 }}>
                            {shortenUserAgent(device.userAgent)}
                          </div>
                        )}
                      </td>
                      <td className="small nowrap">{formatDateTime(device.lastSeenAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {device.revokedAt ? (
                          <span className="badge skipped">gesperrt</span>
                        ) : (
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => {
                              if (confirm('Gerät sperren? Der Mitarbeiter muss sich danach neu einladen lassen.')) {
                                revoke.mutate(device.id)
                              }
                            }}
                          >
                            Sperren
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <DeleteStaffModal
        staffId={deleting ? (id ?? null) : null}
        onClose={() => setDeleting(false)}
        onDeleted={() => navigate('/mitarbeiter', { replace: true })}
      />
      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async (message) => {
          if (message) toast(message)
          setOpenTask(null)
          await queryClient.invalidateQueries({ queryKey: ['staff-detail', id] })
        }}
      />
    </>
  )
}

/** "Heute", "Morgen", otherwise the weekday, the date itself sits below it. */
function dayHeading(date: string, today: string): string {
  const relative = relativeDayLabel(date, today)
  return relative === formatDate(date) ? formatWeekday(date, 'long') : relative
}

/** Browser user agents are unreadable; show just the device family. */
function shortenUserAgent(userAgent: string): string {
  if (/iPhone/i.test(userAgent)) return 'iPhone'
  if (/iPad/i.test(userAgent)) return 'iPad'
  if (/Android/i.test(userAgent)) {
    const model = /;\s([^;)]+)\sBuild/.exec(userAgent)?.[1]
    return model ? `Android, ${model}` : 'Android'
  }
  if (/Windows/i.test(userAgent)) return 'Windows'
  if (/Mac OS X/i.test(userAgent)) return 'Mac'
  return userAgent.slice(0, 40)
}
